import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { Component, Inject, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormArray } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnAlarm, SnUser, SnTeam } from '@lib/api/entities';
import { SnAlarmsAPI, SnUsersAPI, SnTeamsAPI } from '@lib/api/services';
import { SnFormFieldConfig } from '@lib/form';
import { t } from '@lib/translation';

@Component({
  templateUrl: './alarm-send-dialog.component.html',
})
export class AlarmSendDialogComponent implements OnDestroy {
  public form: UntypedFormGroup = new UntypedFormGroup({ alarms: new UntypedFormArray([]) });
  public formFields: SnFormFieldConfig[] = [
      {
        key: 'users',
        type: 'chips-autocomplete',
        templateOptions: {
          labelProp: 'fullName',
          label: t('map.sendAlertDialog.usersLabel'),
          fetchDataFn: this._fetchUserData.bind(this),
          separatorKeysCodes: [ENTER, COMMA],
        },
      },
      {
        key: 'teams',
        type: 'chips-autocomplete',
        templateOptions: {
          labelProp: 'name',
          label: t('map.sendAlertDialog.teamsLabel'),
          fetchDataFn: this._fetchTeamData.bind(this),
          separatorKeysCodes: [ENTER, COMMA],
        },
      },
    ];
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _dialogRef: MatDialogRef<AlarmSendDialogComponent>,
    private readonly _alarmsAPI: SnAlarmsAPI,
    private readonly _usersAPI: SnUsersAPI,
    private readonly _teamsAPI: SnTeamsAPI,
    @Inject(MAT_DIALOG_DATA) public data: SnAlarm,
  ) {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submitForm(): void {
    if (this.form.invalid) {
      return;
    }

    const formData = this.form.value;

    this._alarmsAPI
      .sendAlarmTo(this.data.id, {
        users: formData.users?.map((user) => user.id),
        teams: formData.teams?.map((team) => team.id),
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((alarm) => this._dialogRef.close(alarm));
    this.form.reset();
  }

  public cancel(): void {
    this._dialogRef.close();
  }

  private _fetchUserData(params: ISnListQueryParameters): Observable<ISnListResponse<SnUser>> {
    return this._usersAPI.getUserList(params);
  }
  private _fetchTeamData(params: ISnListQueryParameters): Observable<ISnListResponse<SnTeam>> {
    return this._teamsAPI.getTeamList(params);
  }
}
